/**
 * Top5Section.tsx — the Weekly Top 5 tools SECTION (RT-F01). It is a SUBSECTION embedded on Home AND
 * on the Tools/Catalogue page — never a page or a route of its own — so it takes the already-loaded
 * LeaderboardView as a prop and renders inline.
 *
 * GRACEFUL DEGRADATION (RT-F06): fewer than five tools → render exactly what exists, NEVER padded;
 * zero tools → render NOTHING (return null, so the host page simply shows nothing). The section
 * self-hides, so a caller can drop it in unconditionally.
 *
 * EMPTY-STATE OPT-IN: a caller that wants the section to stay VISIBLE on an empty board (so the
 * feature can be shown and explained before real adoption data exists) passes `showWhenEmpty`. This
 * renders the heading + intro with a short "no tools yet this week" placeholder INSTEAD of hiding —
 * it never fabricates rows (RT-F06 still holds: nothing is padded, no fake ranking). Default stays
 * self-hide, so every other host (the Catalogue) is unchanged.
 *
 * MOVEMENT IS TEXT (RT-F04 / RT-NFR04): each row states its movement in words via movementText() — the
 * authoritative signal. A small ▲/▼ glyph rides along purely as decoration (aria-hidden); it is never
 * the ONLY signal and never colour-only (WCAG). The text stands alone.
 *
 * PRIVACY / ANTI-RANKING (RT-NFR05 / RT-F22): every figure on a card is a COUNT or an AVERAGE over the
 * tool — never who adopted/liked/rated. Nothing here can name a person.
 *
 * EXPLAINABILITY (RT-NFR08): the footer carries the backend's one-line explanation and a readable
 * "last updated" from the ISO lastRecalculatedAt (omitted when the string is empty — an empty view,
 * or a run that never happened).
 *
 * ACCESSIBILITY: a labelled <section>, an ordered <ol> for the ranked list (real numbering semantics),
 * and an <h2> heading — this is a subsection of a page whose own title is an <h1>.
 */
import { Link } from 'react-router';
import { formatAdopterCount } from '../tools/trustSignals';
import { movementText, type LeaderboardRow, type LeaderboardView } from './api';

/** The section's scoped styles, over the frame's CSS tokens (App.tsx :root) — theme-aware, no polish. */
const TOP5_STYLES = `
  .tb-top5 { max-width: 60rem; }
  .tb-top5 h2 { font-size: 1.05rem; margin-bottom: 0.25rem; }
  .tb-top5__intro { color: var(--tb-muted); font-size: 0.875rem; margin: 0 0 0.75rem; max-width: 60ch; }
  .tb-top5__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .tb-top5__row {
    display: flex; align-items: baseline; gap: 0.75rem;
    border: 1px solid var(--tb-line); border-radius: 8px;
    padding: 0.6rem 0.85rem; background: var(--tb-surface);
  }
  .tb-top5__rank { font-size: 1.1rem; font-weight: 700; color: var(--tb-accent-ink); flex: 0 0 auto; min-width: 1.75rem; }
  .tb-top5__body { flex: 1 1 auto; min-width: 0; }
  .tb-top5__name { font-weight: 600; }
  .tb-top5__desc { color: var(--tb-ink); margin: 0.15rem 0; }
  .tb-top5__meta { color: var(--tb-muted); font-size: 0.8125rem; display: flex; flex-wrap: wrap; gap: 0.25rem 0.75rem; margin-top: 0.15rem; }
  .tb-top5__move { font-weight: 600; }
  .tb-top5__foot { color: var(--tb-muted); font-size: 0.8125rem; margin: 0.75rem 0 0; max-width: 60ch; }
  .tb-top5__empty {
    border: 1px dashed var(--tb-line); border-radius: 8px;
    padding: 0.85rem 1rem; background: var(--tb-surface);
    color: var(--tb-muted); font-size: 0.9375rem; max-width: 60ch;
  }
`;

/** Format an ISO timestamp readably, or '' when absent/unparseable (the footer then omits the date). */
function readableWhen(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/**
 * One ranked card (RT-F08): position, name→detail link, one-line description, builder credit, primary
 * category (when non-null), adoption count, like count, average rating where enough (else "Not yet
 * rated"), and movement AS TEXT with a decorative arrow.
 *
 * THE ADOPTION COUNT IS WHOLE-LIFE, and this card used to say "adopted this week" over it — a tool
 * adopted forty times across a year, ranking this week, claimed forty adoptions this week. The number
 * was never windowed: `getTop5ForViewerWith` reads it from `adopterCountsSql()` with no window, the
 * same fragment the catalogue cards use (`db/leaderboard.ts` step 5b).
 *
 * The wording was corrected rather than the number, because THIS SECTION IS EMBEDDED ON THE CATALOGUE
 * PAGE as well as on Home (`Catalogue.tsx` renders it above the results). A windowed figure here would
 * put two different counts for one tool on one screen, a few inches apart, which is precisely what
 * AE-F19 and DS-NFR06 exist to prevent. The week is not left unsaid either way: the movement line
 * carries it in words (RT-F04), and it is the RANK that is weekly, not the tool's history.
 *
 * `formatAdopterCount` with no window is therefore the same sentence a homepage all-time card shows,
 * and the same number the catalogue card and the tool page show.
 */
function Top5Card({ row }: { row: LeaderboardRow }) {
  const move = movementText(row.movement, row.movementPlaces);
  // A decorative glyph only — the text above carries the meaning (RT-NFR04, never colour/shape-only).
  const arrow = row.movement === 'up' ? '▲' : row.movement === 'down' ? '▼' : '';
  return (
    // `value` carries the REAL position (RT-F03) so the <ol>'s semantic numbering matches the visible
    // "#n" even when the survivor set is re-numbered by the read path.
    <li className="tb-top5__row" value={row.position}>
      <span className="tb-top5__rank" aria-hidden="true">
        #{row.position}
      </span>
      <div className="tb-top5__body">
        <div className="tb-top5__name">
          {/* The position is decorative above; state it in the accessible name too so a screen-reader
              user hears the rank with the tool. */}
          <Link to={`/tools/${row.toolId}`} aria-label={`Number ${row.position}: ${row.name}`}>
            {row.name}
          </Link>
        </div>
        <p className="tb-top5__desc">{row.description}</p>
        <div className="tb-top5__meta">
          <span>by {row.ownerName}</span>
          {row.primaryCategoryLabel && <span>{row.primaryCategoryLabel}</span>}
          <span>{formatAdopterCount(row.adoptionCount)}</span>
          <span>
            {row.likeCount} {row.likeCount === 1 ? 'like' : 'likes'}
          </span>
          <span>
            {row.ratingAverage !== null
              ? `★ ${row.ratingAverage} (${row.ratingCount})`
              : 'Not yet rated'}
          </span>
          <span className="tb-top5__move">
            {arrow && <span aria-hidden="true">{arrow} </span>}
            {move}
          </span>
        </div>
      </div>
    </li>
  );
}

/**
 * The Weekly Top 5 tools section. Renders null when there are no entries (RT-F06 zero-case) so a host
 * page can include it unconditionally — UNLESS `showWhenEmpty` is set, in which case an empty board
 * renders the heading + a short placeholder (no fabricated rows). Otherwise a labelled section with a
 * numbered <ol> using the REAL `position` numbers (RT-F03) and the explanation + last-updated footer
 * (RT-NFR08).
 */
export function Top5Section({
  view,
  showWhenEmpty = false,
}: {
  view: LeaderboardView;
  showWhenEmpty?: boolean;
}) {
  if (view.entries.length === 0) {
    if (!showWhenEmpty) return null;
    // Visible empty state: the section stays on the page so it can be shown/explained, but it never
    // invents a ranking — it states plainly that no tool has qualified yet this week (RT-F06).
    return (
      <section aria-labelledby="top5-heading" className="tb-top5" style={{ marginTop: '2rem' }}>
        <style>{TOP5_STYLES}</style>
        <h2 id="top5-heading">Weekly Top 5 tools</h2>
        <p className="tb-top5__intro">The tools colleagues are adopting most this week.</p>
        <p className="tb-top5__empty">
          No tools have made the board yet this week. A tool appears here once colleagues both adopt
          it and like it — the weekly ranking fills in as that activity builds.
        </p>
      </section>
    );
  }

  const updated = readableWhen(view.lastRecalculatedAt);

  return (
    <section aria-labelledby="top5-heading" className="tb-top5" style={{ marginTop: '2rem' }}>
      <style>{TOP5_STYLES}</style>
      <h2 id="top5-heading">Weekly Top 5 tools</h2>
      <p className="tb-top5__intro">The tools colleagues are adopting most this week.</p>
      {/* A real ordered list — the numbering is semantic, not just the visible "#n" glyph. Each item
          carries its REAL `position` via `value` (set in Top5Card) so a re-numbered survivor set
          stays honest. */}
      <ol className="tb-top5__list">
        {view.entries.map((row) => (
          <Top5Card key={row.toolId} row={row} />
        ))}
      </ol>
      <p className="tb-top5__foot">
        {view.explanation}
        {updated && ` Last updated ${updated}.`}
      </p>
    </section>
  );
}
